import { omit } from "lodash-es";
import * as Sentry from "@sentry/react";
import type { OneclubResponse } from "@/types/apis/oneclub";
import { handleUnauthorized } from "@/utils/auth/handleUnauthorized";
import { UnauthorizedError } from "@/utils/auth/UnauthorizedError";
import { sentryMarkAsReported } from "@/utils/sentry.helper";

type FetchOneClubOptions = {
  url: string;
  body?: unknown;
  method?: string;
};

function getGeneralizedPath(url: string): string {
  return url.replace(/\/\d+/g, "/{id}");
}

export const fetchOneClub = async <T>(
  options: FetchOneClubOptions
): Promise<T> => {
  const { url, body, method = "POST" } = options;
  const fullUrl = `${import.meta.env.VITE_ONECLUB_API_URL}${url}`;
  const cleanPath = getGeneralizedPath(url);

  let response: Response;
  try {
    response = await fetch(fullUrl, {
      method,
      headers: {
        "Content-Type": "application/json",
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    Sentry.withScope((scope) => {
      scope.setTag("type", "network_crash");
      scope.setTag("api_source", "oneclub");
      scope.setFingerprint(["network-crash", method, cleanPath]);
      scope.setExtra("full_url", fullUrl);
      scope.setExtra("request_body", body);
      scope.setExtra("method", method);
      Sentry.captureException(e);
    });
    sentryMarkAsReported(e);
    throw e;
  }

  if (response.status === 401) {
    handleUnauthorized();
    throw new UnauthorizedError();
  }

  if (!response.ok) {
    const error = new Error("ERROR");
    Sentry.withScope((scope) => {
      scope.setTag("api_path", cleanPath);
      scope.setTag("api_method", method);
      scope.setTag("api_source", "oneclub");
      scope.setTag("http_status", response.status);
      scope.setFingerprint([method, cleanPath]);
      scope.setExtra("full_url", fullUrl);
      scope.setExtra("request_body", body);
      scope.setLevel("warning");
      Sentry.captureMessage(`API Error: ${method} ${cleanPath} [${response.status}]`);
    });
    sentryMarkAsReported(error);
    throw error;
  }

  const result = (await response.json()) as OneclubResponse<unknown>;

  let errorMessage = "";
  if (result && result.code === "Failure") {
    errorMessage = result.data;
  }

  if (errorMessage) {
    Sentry.withScope((scope) => {
      scope.setTag("api_code", "Failure");
      scope.setTag("api_path", cleanPath);
      scope.setTag("api_method", method);
      scope.setTag("api_source", "oneclub");
      scope.setFingerprint([method, cleanPath]);
      scope.setExtra("full_url", fullUrl);
      scope.setExtra("request_body", body);
      scope.setExtra("error", result);
      scope.setLevel("warning");
      Sentry.captureMessage(`API Error: ${method} ${cleanPath}`);
    });
    const failureError = new Error(errorMessage);
    sentryMarkAsReported(failureError);
    throw failureError;
  }

  return omit(result, "code") as T;
};
